/**
 * Navegação entre as páginas do site.
 *
 * O projeto é MULTI-PÁGINA de verdade: `rollupOptions.input` no `vite.config`
 * lista seis entradas, e cada uma carrega só o próprio código — a mesa não
 * baixa o painel admin junto. O preço disso é que não existe um roteador para
 * pendurar um menu; a navegação precisa ser um módulo que cada página monta.
 *
 * As URLs vão SEM `.html`. Não é maquiagem: o servidor resolve `/replay` para
 * `replay.html` (ver `WEB_DIST` em apps/server/src/config.ts) e o dev do Vite
 * faz o mesmo. Escrever a extensão só amarraria o link ao formato do arquivo.
 */

import { t } from '@/i18n'
import { aplicarIdiomaNoDocumento, ligarSeletorDeIdioma, seletorDeIdiomaHtml } from '@/i18n/seletor'
import { icone, type Icone } from '@/ui/icones'

/**
 * `icone`: só no Treino. Os outros itens são páginas para LER; o treino é o
 * único que leva a jogar, e sem o ícone ele se perdia no meio deles.
 */
type Item = { href: string; rotulo: string; titulo: string; icone?: Icone }

/**
 * O servidor da comunidade.
 *
 * Convite permanente, sem validade e sem limite de usos — um link que expira
 * transforma cada versão publicada do jogo num botão quebrado.
 */
const DISCORD_CONVITE = 'https://discord.gg/JWUfssknvQ'

/** A marca do Discord, inline: um ícone só não vale uma requisição de rede. */
const ICONE_DISCORD = `<svg viewBox="0 0 127.14 96.36" class="size-4 fill-current" aria-hidden="true"><path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.25 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.18c.91-.66 1.8-1.34 2.66-2a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.9-72.14ZM42.45 65.69C36.18 65.69 31 60 31 53s5-12.74 11.43-12.74S54 46 53.89 53s-5.05 12.69-11.44 12.69Zm42.24 0C78.41 65.69 73.25 60 73.25 53s5-12.74 11.44-12.74S96.23 46 96.12 53s-5.04 12.69-11.43 12.69Z"/></svg>`

/*
 * "Mesa" NÃO entra aqui.
 *
 * A página da mesa só faz sentido com partida em andamento; fora disso ela
 * abre dizendo "aguardando uma mesa fechar no lobby". Um item de menu que leva
 * a uma tela vazia ensina o usuário a ignorar o menu.
 *
 * O caminho para a mesa é o botão "Entrar na mesa", no lobby, que aparece
 * exatamente quando existe uma partida para entrar.
 */
const itens = (): Item[] => [
  { href: '/', rotulo: t('nav.lobby'), titulo: t('nav.lobbyTitulo') },
  { href: '/play', rotulo: t('nav.treino'), titulo: t('nav.treinoTitulo'), icone: 'target' },
  { href: '/regras', rotulo: t('nav.regras'), titulo: t('nav.regrasTitulo') },
  { href: '/cofre', rotulo: t('nav.cofre'), titulo: t('nav.cofreTitulo') },
  { href: '/determinism', rotulo: t('nav.determinismo'), titulo: t('nav.determinismoTitulo') },
  { href: '/changelog', rotulo: t('nav.novidades'), titulo: t('nav.novidadesTitulo') },
]

/**
 * A barra, como HTML.
 *
 * Marca a página atual comparando o caminho já sem extensão — assim `/mesa` e
 * `/mesa.html` acendem o mesmo item, e links antigos não ficam órfãos.
 */
export function renderNav(): string {
  const atual = location.pathname.replace(/\.html$/, '').replace(/^\/index$/, '/') || '/'

  const links = itens()
    .map((i) => {
      const ativo = i.href === atual
      return `<a href="${i.href}" title="${i.titulo}" class="nav-link"
         ${ativo ? 'aria-current="page"' : ''}>${i.icone ? icone(i.icone, 14) : ''}${i.rotulo}</a>`
    })
    .join('')

  /*
   * Três zonas, e a ORDEM no HTML é a do desktop: marca, links, ações.
   *
   * No celular o CSS manda os links para a linha de baixo (`order`), em vez de
   * deixar a quebra automática decidir. Ver `.nav-links` em `styles.css`, que
   * é onde este arranjo está explicado.
   */
  return `
    <nav class="nav">
      <!-- A bola sem o nome: "SOL Pool" escrito aqui E no cabecalho do lobby era
           o mesmo nome duas vezes na mesma tela. Ficou o de baixo, que vem com a
           frase do que o jogo e com os selos de estado. O aria-label mantem o
           link anunciado para quem usa leitor de tela. -->
      <a href="/" class="nav-marca" aria-label="SOL Pool">
        <span class="grid size-6 place-items-center rounded-full bg-black text-[11px] font-bold text-white" aria-hidden="true">8</span>
      </a>

      <div class="nav-links">${links}</div>

      <div class="nav-acoes">
        <!-- O convite fica FORA da lista de itens: aqueles são páginas do
             site, e o destaque só faz sentido para o link que sai dele. -->
        <a href="${DISCORD_CONVITE}" target="_blank" rel="noopener"
           title="${t('nav.discordTitulo')}"
           class="flex items-center gap-1.5 rounded-md bg-[#5865F2]/15 px-2.5 py-1.5
                  text-[13px] whitespace-nowrap text-[#c3c9ff] no-underline transition-colors
                  hover:bg-[#5865F2]/25 hover:text-white">
          ${ICONE_DISCORD}<span class="nav-discord-rotulo">${t('nav.discord')}</span>
        </a>
        <!-- O seletor de idioma mora aqui porque a barra está em toda página
             que tem interface de texto: é o lugar que o jogador já olha. -->
        ${seletorDeIdiomaHtml()}
      </div>
    </nav>`
}

/**
 * Insere a barra no topo do documento, antes do conteúdo.
 *
 * Devolve a `<nav>` para a página pendurar o que só ela usa — o admin liga ali
 * o conector de carteira (`ui/conector-de-carteira.ts`). O conector NÃO vem
 * embutido aqui: importá-lo puxaria o Wallet Standard e o web3.js para as
 * páginas que não têm carteira nenhuma (regras, novidades, cofre).
 */
export function montarNav(): Element | null {
  const alvo = document.getElementById('app') ?? document.body
  const wrapper = document.createElement('div')
  wrapper.innerHTML = renderNav()
  const nav = wrapper.firstElementChild
  if (nav) {
    alvo.parentNode?.insertBefore(nav, alvo)
    ligarSeletorDeIdioma(nav)
  }
  aplicarIdiomaNoDocumento()
  return nav
}
